import { toDay } from "../charts/charts.utils";
import { getCountryCode, getCountryLabel } from "../charts/countries.utils";

import type {
  CountryStat,
  DevicePlatformStat,
  GetProjectCountriesResponse,
  SignupsAndSubscriptions,
} from "@/src/api/StatsAPI.types";
import type { CountriesState } from "@/src/reducers/fetch.types";

import type {
  ActiveUsersSeriesKey,
  MapView,
  SignupsSeriesKey,
  TrialingSeriesKey,
} from "./dashboard.types";
import type {
  PaidFreeSeriesKey,
  RankedDatum,
  TimelineDatum,
  UsersSeriesKey,
} from "../charts/charts.types";

// The API has been seen sending a `date` key among the platforms.
const NON_PLATFORM_KEY_LIST = ["total", "date"];

export function buildSignupsTimeline(
  signupsList: readonly SignupsAndSubscriptions[],
): TimelineDatum<SignupsSeriesKey>[] {
  return signupsList.map((signupsDay) => ({
    date: toDay(signupsDay.date),
    values: { signups: signupsDay.signups.total },
  }));
}

export function buildActiveUsersTimeline(
  signupsList: readonly SignupsAndSubscriptions[],
): TimelineDatum<ActiveUsersSeriesKey>[] {
  return signupsList.map((signupsDay) => ({
    date: toDay(signupsDay.date),
    values: { activeUsers: signupsDay.active_users },
  }));
}

export function buildTrialingTimeline(
  signupsList: readonly SignupsAndSubscriptions[],
): TimelineDatum<TrialingSeriesKey>[] {
  return signupsList.map((signupsDay) => ({
    date: toDay(signupsDay.date),
    values: { trialing: signupsDay.subscriptions.trialing },
  }));
}

export function buildSignupsByPlatform(
  signupsList: readonly SignupsAndSubscriptions[],
  getPlatformLabel: (platform: string) => string,
): RankedDatum<SignupsSeriesKey>[] {
  const totalByPlatform = new Map<string, number>();

  signupsList.forEach((signupsDay) => {
    Object.entries(signupsDay.signups).forEach(([platform, count]) => {
      if (NON_PLATFORM_KEY_LIST.includes(platform)) return;
      if (typeof count !== "number") return;
      totalByPlatform.set(
        platform,
        (totalByPlatform.get(platform) ?? 0) + count,
      );
    });
  });

  return [...totalByPlatform]
    .filter(([, total]) => total > 0)
    .map(([platform, total]) => ({
      label: getPlatformLabel(platform),
      values: { signups: total },
    }));
}

export function buildDevicesByPlatform(
  platformStatByName: Readonly<Partial<Record<string, DevicePlatformStat>>>,
  getPlatformLabel: (platform: string) => string,
): RankedDatum<PaidFreeSeriesKey>[] {
  return Object.entries(platformStatByName).flatMap(([platform, stat]) =>
    stat && stat.paid + stat.free > 0
      ? [
          {
            label: getPlatformLabel(platform),
            values: { paid: stat.paid, free: stat.free },
          },
        ]
      : [],
  );
}

function getCountryStatList(
  response: GetProjectCountriesResponse | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
): { code: string; stat: CountryStat }[] {
  return (response?.data ?? []).flatMap((countriesItem) =>
    Object.entries(countriesItem.countries).flatMap(([countryUuid, stat]) => {
      const code = getCountryCode(countries, countryUuid);
      return code ? [{ code, stat }] : [];
    }),
  );
}

export function buildCountriesMapData(
  response: GetProjectCountriesResponse | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
  mapView: MapView,
): Record<string, CountryStat> {
  const data: Record<string, CountryStat> = {};

  getCountryStatList(response, countries).forEach(({ code, stat }) => {
    if (mapView === "users" && !stat.users) return;

    data[code] = stat;
  });

  return data;
}

export function buildCountriesViewsRankedList(
  response: GetProjectCountriesResponse | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
  lang: string,
): RankedDatum<PaidFreeSeriesKey>[] {
  return getCountryStatList(response, countries).flatMap(({ code, stat }) =>
    stat.paid + stat.free > 0
      ? [
          {
            label: getCountryLabel(code, lang),
            values: { paid: stat.paid, free: stat.free },
          },
        ]
      : [],
  );
}

export function buildCountriesUsersRankedList(
  response: GetProjectCountriesResponse | undefined,
  countries: Pick<CountriesState, "data" | "indexes">,
  lang: string,
): RankedDatum<UsersSeriesKey>[] {
  return getCountryStatList(response, countries).flatMap(({ code, stat }) =>
    stat.users > 0
      ? [{ label: getCountryLabel(code, lang), values: { users: stat.users } }]
      : [],
  );
}
